import { Routes } from "@/utils/routes";
import type { IconName } from "@/components/icons/Icon";
import {
  SETTINGS_NAV_ICONS,
  type SettingSidebarItem,
  type SettingsNavbarId,
} from "@/components/settings/settings.constants";

// Settings as the category-rail nav (the `onboard_experience` flag) groups it: Team,
// Environment, Storage, and Infrastructure, with panes for pages that live outside `/settings`.
// The classic Settings grouping is SETTINGS_SIDEBAR_ITEMS in settings.constants.

export const CATEGORY_NAV_SETTINGS_ITEMS = [
  {
    title: "Team",
    panes: [
      {
        id: "billing",
        name: "Billing",
        flag: "stripe_billing_enabled",
        chalkEmployeeOnly: true,
      },
      {
        // note: custom display behavior determined by flag `resource_usage_disabled`
        id: "usage-dashboard",
        name: "Usage",
        shouldShowInternal: true,
      },
      {
        id: "shared-resources",
        name: "Shared resources",
        deprecationNotice: "Shared resources have moved to Infrastructure/cluster-level settings.",
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        // Admin-only without `shouldShowInternal`, which would show it to employees outside admin
        // mode, or the `metadata_argo_workflows_enabled` flag, which would hide it from admins.
        id: "infra-pipelines",
        name: "Infra pipelines",
        chalkEmployeeOnly: true,
      },
      {
        id: "github",
        name: "GitHub",
      },
    ],
  },
  {
    title: "Environment",
    panes: [
      {
        id: "general",
        name: "General",
      },
      {
        id: "permissions",
        name: "Users and roles",
      },
      {
        id: "tokens",
        name: "Access tokens",
      },
      {
        // Lives with the access controls above; the page itself stays under /logs.
        id: "audit-logs",
        name: "Audit logs",
        searchTerms: ["audit trail", "activity log"],
        href: Routes.project.environment.logs.audit,
      },
      {
        id: "cli-login",
        name: "CLI login",
        chalkEmployeeOnly: true,
      },
      {
        id: "secrets",
        name: "Secrets & variables",
        searchTerms: [
          "secrets",
          "variables",
          "environment variables",
          "env vars",
          "runtime variables",
          "runtime environment",
        ],
        href: Routes.project.environment.secrets,
      },
      {
        id: "environment-variables",
        name: "Config variables",
        searchTerms: [
          "environment variables",
          "config variables",
          "configuration variables",
          "env vars",
        ],
        href: Routes.project.environment.envVariables,
      },
      {
        id: "assistant",
        name: "Assistant",
        searchTerms: [
          "conversations",
          "chat history",
          "model providers",
          "ai providers",
          "ai connections",
        ],
      },
      {
        id: "compute",
        name: "Compute",
        searchTerms: ["host pools"],
        hideInServerless: true,
      },
      {
        id: "quotas",
        name: "Quotas",
        flag: "quotas_enabled",
        searchTerms: ["limits", "gpu quota", "quota increase", "sandbox limit"],
      },
      {
        id: "host-capacity",
        name: "Host capacity",
        searchTerms: ["host pools", "sandbox capacity", "unschedulable", "hosts", "external hosts"],
        chalkEmployeeOnly: true,
      },
      {
        id: "feature-flags",
        name: "Feature flags",
        chalkEmployeeOnly: true,
      },
      {
        id: "notebooks",
        name: "Notebooks",
        flag: "notebooks_enabled",
      },
      {
        id: "suspend",
        name: "Suspend",
      },
      {
        id: "archive",
        name: "Archive",
        chalkEmployeeOnly: true,
      },
      {
        // Clears out a self-signup team so the tester's email is free again.
        // Employee-only in the menu; the endpoint enforces chalk.admin itself.
        id: "delete-team",
        name: "Delete team",
        searchTerms: ["self-signup", "signup"],
        chalkEmployeeOnly: true,
      },
    ],
  },
  {
    // Storage configuration outside the Context integrations group.
    title: "Storage",
    panes: [
      {
        id: "vector-db",
        name: "Vector store",
        searchTerms: ["vector database", "vector db"],
        href: Routes.project.environment.vectorDB,
      },
      {
        id: "buckets",
        name: "Buckets",
      },
    ],
  },
  {
    title: "Infrastructure",
    panes: [
      {
        id: "connections",
        name: "Connections",
        chalkEmployeeOnly: true,
      },
      {
        id: "kubernetes",
        name: "Kubernetes",
        searchTerms: ["pods", "nodes", "service accounts"],
        href: Routes.project.environment.kubernetes,
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        id: "nodepools",
        name: "Nodepools",
        searchTerms: ["karpenter"],
        href: Routes.project.environment.nodepools,
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        // Shown to everyone, as the classic nav's Infrastructure group does: readers size their
        // deployment's resources here.
        id: "resources",
        name: "Resources",
      },
      {
        id: "resource-group-jobs",
        name: "Resource group jobs",
        searchTerms: ["job queue", "resource group job queue"],
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        id: "cloud-overview",
        name: "Cloud overview",
        searchTerms: ["architecture", "cloud architecture", "cloud resources"],
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        id: "artifact-repositories",
        name: "Artifact repositories",
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        id: "metrics-database",
        name: "Metrics database",
        href: Routes.project.environment.metricsDatabase,
        environmentResourceIdField: "clusterTimescaledbId",
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        id: "background-persistence",
        name: "Background persistence",
        href: Routes.project.environment.backgroundPersistence,
        environmentResourceIdField: "backgroundPersistenceDeploymentId",
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        id: "workflow-orchestrator-engine",
        name: "Workflow orchestrator engine",
        href: Routes.project.environment.workflowOrchestratorEngine,
        environmentResourceIdField: "clusterWorkflowOrchestratorId",
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        id: "clickhouse",
        name: "ClickHouse info",
        searchTerms: ["clickhouse", "otel", "telemetry", "ttl", "storage", "logs", "traces"],
        hideInServerless: true,
        chalkEmployeeOnly: true,
      },
      {
        id: "build-profile",
        name: "Build profile",
        chalkEmployeeOnly: true,
      },
    ],
  },
] as const;

export const CATEGORY_NAV_SETTINGS_ITEMS_FLAT: SettingSidebarItem[] =
  CATEGORY_NAV_SETTINGS_ITEMS.flatMap((pane): readonly SettingSidebarItem[] => pane.panes);

/** Icons for the category nav's Settings panes: the classic icons plus its added panes'. */
export const CATEGORY_NAV_SETTINGS_ICONS: Partial<Record<SettingsNavbarId, IconName>> = {
  ...SETTINGS_NAV_ICONS,
  data_sources: "database",
  "online-store": "online",
  "offline-store": "circlePile",
  "vector-db": "arrowUp10",
  secrets: "bookKey",
  "environment-variables": "environment",
  "audit-logs": "textSearch",
  kubernetes: "shipWheel",
  nodepools: "vectorSquare",
  resources: "wrench",
  "resource-group-jobs": "listVideo",
  "cloud-overview": "cloudServer",
  "artifact-repositories": "package",
  "metrics-database": "database",
  "background-persistence": "hardDrive",
  "workflow-orchestrator-engine": "route",
  clickhouse: "database",
  "build-profile": "radar",
};
